import React from 'react';
import PropTypes from 'prop-types';
import { TabContent, TabPane } from 'reactstrap';
import { getQuery } from 'client/utils/location';
import { noop } from 'lodash-es';
import { VIN_TAB } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';
import { useAppraisalTabsConfig } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';
import { LicensePlateTab } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/license-plate-tab/license-plate-tab';
import { buildAppraisalTabView } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tab-view';
import { AppraisalTabLink } from './appraisal-tab-link';

/**
 * Mixed consumers — they need both config flags and activeTabId/tabs from state. They use both hooks separately rather than the convenience useAppraisalTabsContext() to keep the intent explicit.
 */
export function TabsContent({
  activeTabId,
  tabs,
  storage = null,
  onTabLinkClick,
  onPrepopulatedVehicle,
  onAddressVin,
  ctaText = undefined,
  ctaClassName = undefined,
  isYearDropdownFirst = true,
  initialStateCode = '',
  initialVehicle = null,
  setStylesAndVin = noop,
  isProfileOfferVinPrepopulationEnabled = false,
  profileOfferVin = '',
  hasAddressToVinDrawer = false,
  setModelValue = noop,
  isPrivatePartyBtn = false,
}) {
  const {
    isLookUpStyleView,
    isMobile,
    isSellCar,
    isSellInCityState,
    isWIDG1125chal1,
    isVdpEmbedded,
    isAppraisalLanding,
    isBackgroundStyleTabEnabled,
    isEmoSearchEnabled,
    creativeId,
    location,
    isFullWidth,
  } = useAppraisalTabsConfig();
  const query = getQuery(location);
  const vinTabLink = <AppraisalTabLink onClick={onTabLinkClick} tabLink={VIN_TAB} />;
  const view = buildAppraisalTabView({
    isMobile,
    isSellCar,
    isSellInCityState,
    isWIDG1125chal1,
    isVdpEmbedded,
    isLookUpStyleView,
    isFullWidth,
    isProfileOfferVinPrepopulationEnabled,
    profileOfferVin,
    hasAddressToVinDrawer,
    isBackgroundStyleTabEnabled,
    isEmoSearchEnabled,
    isAppraisalLanding,
    isPrivatePartyBtn,
  });

  return (
    <TabContent activeTab={activeTabId}>
      {tabs.map(({ id, TabComponent }) =>
        activeTabId === id ? (
          <TabPane tabId={id} key={`tab-${id}`} data-testid={`tab-${id}`}>
            <TabComponent
              handlePrepopulatedVehicle={onPrepopulatedVehicle}
              creativeId={creativeId}
              ctaText={ctaText}
              btnClassName={ctaClassName}
              query={query}
              isYearDropdownFirst={isYearDropdownFirst}
              initialStateCode={initialStateCode}
              initialVehicle={initialVehicle}
              setStylesAndVin={setStylesAndVin}
              storage={storage}
              isActiveTab={activeTabId === id}
              vinTabLink={vinTabLink}
              activeTabId={activeTabId}
              updateVinAndContinue={onAddressVin}
              setModelValue={setModelValue}
              view={view}
              licensePlateAndVinForm={
                <LicensePlateTab
                  storage={storage}
                  ctaText="Continue"
                  query={query}
                  setStylesAndVin={setStylesAndVin}
                  vinTabLink={vinTabLink}
                  isActiveTab={activeTabId === id}
                  view={buildAppraisalTabView({
                    isMobile,
                    isFullWidth: true,
                    isLookUpStyleView,
                    isBackgroundStyleTabEnabled,
                    isEmoSearchEnabled,
                    isAddressTab: true,
                    isVinByAddressOverlay: true,
                  })}
                />
              }
            />
          </TabPane>
        ) : null
      )}
    </TabContent>
  );
}

TabsContent.propTypes = {
  activeTabId: PropTypes.string.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      TabComponent: PropTypes.elementType.isRequired,
    })
  ).isRequired,
  onTabLinkClick: PropTypes.func.isRequired,
  onPrepopulatedVehicle: PropTypes.func.isRequired,
  onAddressVin: PropTypes.func.isRequired,
  ctaText: PropTypes.string,
  ctaClassName: PropTypes.string,
  isYearDropdownFirst: PropTypes.bool,
  initialStateCode: PropTypes.string,
  initialVehicle: PropTypes.shape({}),
  setStylesAndVin: PropTypes.func,
  isProfileOfferVinPrepopulationEnabled: PropTypes.bool,
  profileOfferVin: PropTypes.string,
  hasAddressToVinDrawer: PropTypes.bool,
  setModelValue: PropTypes.func,
  storage: PropTypes.shape({}),
  isPrivatePartyBtn: PropTypes.bool,
};
